"use client";
import { useState, useRef, useContext } from "react";
import { cn } from "@utils/utils";
import { HorizontalTabContext } from "@context/HorizontalTabContext";
import styles from "./HorizontalTab.module.scss";

interface HorizontalTabProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

export function HorizontalTab({
  defaultValue,
  value: controlledValue,
  onValueChange,
  children,
  className,
}: HorizontalTabProps) {
  const [internalValue, setInternalValue] = useState(defaultValue || "");
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const handleValueChange = (newValue: string) => {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  return (
    <HorizontalTabContext.Provider
      value={{ value, onValueChange: handleValueChange }}
    >
      <div className={cn(styles["horizontal-tab"], className)}>{children}</div>
    </HorizontalTabContext.Provider>
  );
}

interface HorizontalTabListProps {
  children: React.ReactNode;
  className?: string;
}

export function HorizontalTabList({
  children,
  className,
}: HorizontalTabListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const triggers = listRef.current?.querySelectorAll(
      '[role="tab"]'
    ) as NodeListOf<HTMLElement>;
    if (!triggers) return;

    const currentIndex = Array.from(triggers).findIndex(
      (trigger) => trigger === document.activeElement
    );

    let nextIndex = currentIndex;

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : triggers.length - 1;
        break;
      case "ArrowRight":
        event.preventDefault();
        nextIndex = currentIndex < triggers.length - 1 ? currentIndex + 1 : 0;
        break;
      case "Home":
        event.preventDefault();
        nextIndex = 0;
        break;
      case "End":
        event.preventDefault();
        nextIndex = triggers.length - 1;
        break;
      default:
        return;
    }

    triggers[nextIndex]?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      className={cn(styles["horizontal-tab__list"], className)}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
}

interface HorizontalTabTriggerProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

function useHorizontalTabContext() {
  const context = useContext(HorizontalTabContext);
  if (!context) {
    throw new Error(
      "useHorizontalTabContext must be used within a HorizontalTab.Provider"
    );
  }
  return context;
}

export function HorizontalTabTrigger({
  value,
  children,
  className,
  disabled,
}: HorizontalTabTriggerProps) {
  const { value: selectedValue, onValueChange } = useHorizontalTabContext();
  const isSelected = selectedValue === value;

  const handleClick = () => {
    if (!disabled) {
      onValueChange(value);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!disabled) {
        onValueChange(value);
      }
    }
  };

  return (
    <button
      role="tab"
      aria-selected={isSelected}
      aria-controls={`tabpanel-${value}`}
      tabIndex={isSelected ? 0 : -1}
      disabled={disabled}
      className={cn(
        styles["horizontal-tab__trigger"],
        isSelected && styles["horizontal-tab__trigger--selected"],
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </button>
  );
}

interface HorizontalTabContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function HorizontalTabContent({
  value,
  children,
  className,
}: HorizontalTabContentProps) {
  const { value: selectedValue } = useHorizontalTabContext();
  const isSelected = selectedValue === value;

  if (!isSelected) return null;

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${value}`}
      aria-labelledby={`tab-${value}`}
      tabIndex={0}
      className={cn(styles["horizontal-tab__content"], className)}
    >
      {children}
    </div>
  );
}
